import React from 'react'
import PropTypes from 'prop-types'
import { Tooltip, useMediaQuery, Chip } from '@material-ui/core'
import { makeStyles, useTheme, withStyles } from '@material-ui/core/styles'
import { isNil, prop } from 'ramda'
import TruncatedText from 'app/componentV4/TruncatedText'
import { FormattedMessage, useIntl } from 'react-intl'
import { isEmptyOrNil } from 'libs/empty'
import { getHumanizeDate } from 'commons/date'
import { channelName } from 'app/componentV4/Channel/ChannelsName'
import StatusChip from 'app/componentV4/Status/Chip'
import ChannelIcon from 'app/componentV4/Channel/ChannelIcon'
import { subscriberStatus } from 'app/constants/subscriberCRM'
import { useAppName } from 'commons/usePartner'
import { twMerge } from 'tailwind-merge'
import Assets from 'app/configs/assetConfigs'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid'
import Restricted from 'app/componentV4/Restricted'
import { PersonAddIcon } from 'app/components/icons'
import LaunchIcon from '@material-ui/icons/Launch'
import { useHistory } from 'react-router-dom'
import { Routes } from 'app/services/routeService/routeService'

const useStyles = makeStyles((theme) => ({
  black: {
    backgroundColor: '#071E3D',
  },
  navy: {
    backgroundColor: '#1F4287',
  },
  cyan: {
    backgroundColor: '#0E9CBC',
  },
  purple: {
    backgroundColor: '#7045AF',
  },
  magenta: {
    backgroundColor: '#E14594',
  },
  orange: {
    backgroundColor: '#E18745',
  },
}))
const bgColorSet = ['black', 'navy', 'cyan', 'purple', 'magenta', 'orange']

const CustomsTooltip = withStyles((theme) => ({
  tooltip: {
    color: theme.colors.primary,
    boxShadow: theme.shadows[1],
    backgroundColor: theme.palette.common.white,
    maxWidth: 450,
    fontSize: '0.875rem',
  },
}))(Tooltip)

const SubscriberTooltip = ({ subscriber, text, ...rest }) => {
  const history = useHistory()
  const theme = useTheme()
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'))
  const { formatMessage } = useIntl()
  const appName = useAppName()
  const avatarClasses = useStyles()

  const tooltipTitle = (
    <div className="w-full" onClick={(e) => e.stopPropagation()}>
      <div className="flex p-4 rounded-8 space-x-4">
        <div className="flex text-24">
          {isEmptyOrNil(subscriber.avatar) ? (
            <div
              className="flex justify-center items-center font-bold text-white"
              style={{ width: '72px', height: '72px' }}
            >
              <div
                className={twMerge(
                  avatarClasses[getBgClassName(subscriber.id)],
                  'w-full h-full object-cover rounded-8 flex items-center justify-center',
                )}
              >
                {subscriber.avatarCharacters}
              </div>
            </div>
          ) : (
            <div
              className="flex font-bold text-white"
              style={{ width: '72px', height: '72px' }}
            >
              <img
                src={subscriber.avatar}
                className="w-full object-cover rounded-8"
                alt="Subscriber profile"
                onError={(e) => {
                  e.target.onerror = null
                  e.target.src = Assets.personSilhouetteImage
                }}
              />
            </div>
          )}
        </div>
        <div className="w-full p-2 px-4">
          <div className="bg-blue-500 text-16 font-bold text-blue-950">
            <TruncatedText
              text={[
                prop('firstName', subscriber),
                prop('middleName', subscriber),
                prop('lastName', subscriber),
              ].join(' ')}
              maxLength={isDownXs ? 35 : 40}
            />
            <div className="flex-1 text-12 text-neutrals-500 mt-4 py-4">
              <FormattedMessage
                id="subscriberDetail.lastSeenDate.label"
                values={{
                  date: isEmptyOrNil(subscriber.lastSeen)
                    ? '--'
                    : getHumanizeDate(subscriber.lastSeen),
                }}
              />
            </div>
          </div>

          <div className="py-2 flex space-x-2">
            <Chip
              label={channelName(subscriber.channel, appName)}
              variant="outlined"
              icon={
                <div className="h-auto flex h-10">
                  <ChannelIcon channel={subscriber.channel} size={14} />
                </div>
              }
              className="w-auto bg-primary-200 text-primary-300 border-primary-350 text-12 h-10"
            />
            {!isNil(prop('status', subscriber)) && (
              <StatusChip
                type={subscriberStatus[subscriber.status].type}
                label={formatMessage({
                  id: subscriberStatus[subscriber.status].label,
                })}
                className="h-10"
              />
            )}
          </div>
        </div>
      </div>

      <div className="w-full px-4 space-y-2 text-neutrals-500 text-12">
        {subscriber.hideContacts === 1 ? (
          <>
            <div className="flex items-center">
              <MailOutlineIcon fontSize="small" className="text-neutrals-500" />
              <div className="px-4">
                <Restricted />
              </div>
            </div>
            <div className="flex items-center">
              <PhoneAndroidIcon
                fontSize="small"
                className="text-neutrals-500"
              />
              <div className="px-4">
                <Restricted />
              </div>
            </div>
          </>
        ) : (
          <>
            {!isEmptyOrNil(subscriber.email) && (
              <div className="flex items-center">
                <MailOutlineIcon
                  fontSize="small"
                  className="text-neutrals-500"
                />
                <div className="px-4">{subscriber.email}</div>
              </div>
            )}
            {!isEmptyOrNil(subscriber.phoneNumber) && (
              <div className="flex items-center">
                <PhoneAndroidIcon
                  fontSize="small"
                  className="text-neutrals-500"
                />
                <div className="px-4">{subscriber.phoneNumber}</div>
              </div>
            )}
          </>
        )}

        <div className="flex items-center">
          <PersonAddIcon classes="mx-1 text-neutrals-500" />
          <div className="px-4 capitalize">
            {formatMessage(
              {
                id: 'subscriberCrm.count.groups',
              },
              {
                count: subscriber?.tags?.length,
              },
            )}
          </div>
        </div>
      </div>
      <div className="mt-5 p-4 text-13">
        <div
          className="w-fit text-secondary cursor-pointer flex items-center"
          onClick={() =>
            history.push(Routes.viewSubscriberDetail(subscriber.id))
          }
        >
          <FormattedMessage id="edit.detail.text" />
          <LaunchIcon className="text-15 mx-4" />
        </div>
      </div>
    </div>
  )

  return (
    <CustomsTooltip
      title={tooltipTitle}
      interactive
      arrow
      placement="right-start"
      {...rest}
    >
      <span style={{ cursor: 'pointer' }}>{text}</span>
    </CustomsTooltip>
  )
}

SubscriberTooltip.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  maxLength: PropTypes.number,
  onActionClick: PropTypes.func,
}

const getBgClassName = (subscriberId) => {
  return parseInt(subscriberId)
    ? bgColorSet[parseInt(subscriberId) % bgColorSet.length]
    : bgColorSet[0]
}

export default SubscriberTooltip
